import React from "react";
import { trackEvent } from "~/utils/analytics";

export type Term = { term: string; definition: string };
export type SectionLink = {
  sectionId: string;
  sectionName: string;
  firstPageId: string;
};
export type BatesLink = {
  batesNumber: string;
  pageId: string;
};

/**
 * Convert literal \n sequences (backslash + n) in API responses
 * to actual newline characters for proper Markdown rendering.
 */
export const normalizeLiteralNewlines = (content: string): string => {
  if (!content) return content;
  return content.replace(/\\n/g, "\n");
};

export const unwrapAllCodeBlocks = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);

    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx);
        const keptBefore = out.slice(0, blockStartIdx);
        out.length = 0;
        out.push(...keptBefore, ...blockLines);

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};
/**
 * Ensure markdown block-level markers (list items and headings) that appear
 * mid-line get proper newlines inserted before them.
 */
export const ensureMarkdownNewlines = (content: string): string => {
  if (!content) return content;

  let result = content;

  // Insert newline before unordered list markers (- * +)
  result = result.replace(/([.!?:;,)]|(?<!\w)\])(?!\n) ?([-*+] )/g, "$1\n$2");

  // Insert newline before ordered list markers (1. 2. etc.) — limit to 1-3 digit numbers
  // to avoid matching years like "2019." as list markers
  result = result.replace(/([!?\]:;,)])(?!\n) ?(\d{1,3}\. )/g, "$1\n$2");
  result = result.replace(/(?<!\d)(\.)(?!\n) ?(\d{1,3}\. )/g, "$1\n$2");

  // After closing bold markers (**), insert newline before list markers
  result = result.replace(/(\*\*) ([-*+] )/g, "$1\n$2");
  result = result.replace(/(\*\*) (\d{1,3}\. )/g, "$1\n$2");

  result = result.replace(/([^\n]) +(#{1,6} )/g, "$1\n\n$2");

  return result;
};

export const ensureBoldHeadingSpacing = (content: string): string => {
  if (!content) return content;

  let result = content;

  result = result.replace(/(\*\*\w+(?:\s+\w+)+\*\*)([A-Z])/g, "$1\n\n$2");

  result = result.replace(
    /([.!?])[ \t]*(?:\n(?!\n))?[ \t]*((?:-\s*)?\*\*[A-Z][^*]*\*\*)/g,
    "$1\n\n$2"
  );

  result = result.replace(/\n{3,}/g, "\n\n");

  return result;
};

// `prose-ol:ps-10` widens the ordered-list marker gutter so 3+ digit markers
// (e.g. `100.`) are not clipped by the `prose-sm` default `padding-inline-start`.
// See SKY-2373 — the "APPENDIX A: DOCUMENT LIST" in report view renders via
// UnifiedMarkdown and needs to accommodate lists with more than 99 items.
export const TERMS_PROSE_CLASS =
  "prose prose-sm dark:prose-invert font-sans normal-case prose-ol:ps-10";

// Reference context is fed to the context-mode bounding-box endpoint, whose
// query length is capped at 1000 characters.
const MAX_REFERENCE_CONTEXT_LENGTH = 1000;

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ");

const CITATION_LINK_SELECTOR =
  "a.source-map-link, a.bates-link, a.section-link";

// Index just past the last sentence-ending punctuation (".", "!", "?"), i.e.
// the start of the final sentence in `text`. Returns 0 when there is none.
function lastSentenceStart(text: string): number {
  const boundary = /[.!?]\s+/g;
  let start = 0;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(text)) !== null) {
    start = match.index + match[0].length;
  }
  return start;
}

/**
 * Collect the block's text on either side of the clicked link, omitting the
 * label text of the clicked link and of any sibling citation links. Reading
 * from the DOM (rather than string-searching the label) anchors on the element
 * the user actually clicked, and dropping neighbouring citation labels keeps
 * markers like "[1]" from masking sentence boundaries between citations.
 */
function splitTextAroundLink(
  block: HTMLElement,
  link: HTMLElement
): { before: string; after: string } {
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let before = "";
  let after = "";
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    const isCitationLabel =
      link.contains(node) || !!parent?.closest(CITATION_LINK_SELECTOR);
    if (!isCitationLabel) {
      const relation = link.compareDocumentPosition(node);
      if (relation & Node.DOCUMENT_POSITION_FOLLOWING) {
        after += node.textContent ?? "";
      } else {
        before += node.textContent ?? "";
      }
    }
    node = walker.nextNode();
  }
  return { before, after };
}

/**
 * Extract the sentence/idea surrounding a clicked reference link so it can be
 * used to highlight the relevant region of the source page (context heatmap).
 * Falls back to the enclosing block's text when a single sentence can't be
 * isolated.
 */
export function extractReferenceContext(link: HTMLElement): string {
  const block =
    link.closest<HTMLElement>(
      "p, li, td, th, blockquote, h1, h2, h3, h4, h5, h6"
    ) ?? link.parentElement;
  if (!block) {
    return normalizeWhitespace(link.textContent ?? "")
      .trim()
      .slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
  }

  const { before: rawBefore, after: rawAfter } = splitTextAroundLink(
    block,
    link
  );
  const before = normalizeWhitespace(rawBefore).replace(/\s+$/, "");
  const after = normalizeWhitespace(rawAfter).trim();
  const fullText = normalizeWhitespace(`${rawBefore} ${rawAfter}`).trim();
  if (!fullText) return "";

  // Citation attached to the end of its sentence (e.g. "...no complaints.[1]").
  if (/[.!?]$/.test(before)) {
    const sentence = before.slice(lastSentenceStart(before.slice(0, -1)));
    return (sentence.trim() || fullText).slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
  }

  // Citation embedded mid-sentence: take from the previous boundary in `before`
  // through the next boundary in `after`.
  const head = before.slice(lastSentenceStart(before));
  const afterMatch = after.search(/[.!?](\s|$)/);
  const tail = afterMatch >= 0 ? after.slice(0, afterMatch + 1) : after;
  const sentence = normalizeWhitespace(`${head} ${tail}`).trim();

  return (sentence || fullText).slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
}

/**
 * Event-delegation hook for source-map and bates-link clicks.
 *
 * The optional `context` argument passed to the handlers carries the
 * sentence/idea surrounding the clicked reference, so callers can highlight the
 * relevant area of the source page (see `extractReferenceContext`).
 */
export function useSourceMapDelegation(
  el: HTMLElement | null,
  onSectionClick?: (
    sectionId: string,
    firstPageId: string,
    context?: string
  ) => void,
  onBatesClick?: (pageId: string, context?: string) => void
) {
  React.useEffect(() => {
    if (!el) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as Element;

      const sourceMapLink = target.closest<HTMLElement>("a.source-map-link");
      if (sourceMapLink && el.contains(sourceMapLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: false, sourceType: "section" },
        });
        const sectionId = sourceMapLink.getAttribute("data-section-id");
        const firstPageId =
          sourceMapLink.getAttribute("data-first-page-id") ?? "";
        if (sectionId)
          onSectionClick?.(
            sectionId,
            firstPageId,
            extractReferenceContext(sourceMapLink)
          );
        return;
      }

      const batesLink = target.closest<HTMLElement>("a.bates-link");
      if (batesLink && el.contains(batesLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: true, sourceType: "bates" },
        });
        const pageId = batesLink.getAttribute("data-bates-page-id");
        if (pageId) onBatesClick?.(pageId, extractReferenceContext(batesLink));
        return;
      }

      const sectionLink = target.closest<HTMLElement>("a.section-link");
      if (sectionLink && el.contains(sectionLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: false, sourceType: "section" },
        });
        const sectionId = sectionLink.getAttribute("data-section-id");
        const firstPageId =
          sectionLink.getAttribute("data-first-page-id") ?? "";
        if (sectionId)
          onSectionClick?.(
            sectionId,
            firstPageId,
            extractReferenceContext(sectionLink)
          );
        return;
      }

      // Structured data accordion toggle
      const trailerHeader = target.closest<HTMLElement>(
        ".structured-data-trailer__header"
      );
      if (trailerHeader && el.contains(trailerHeader)) {
        const trailer = trailerHeader.closest(".structured-data-trailer");
        if (trailer) {
          trailer.classList.toggle("open");
        }
        return;
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [el, onSectionClick, onBatesClick]);
}

/**
 * Remove `<a>...</a>` tags from text, keeping only the inner text content.
 * Strip standalone HTML tags (ul, ol, li, b, strong, i, em) while preserving their content.
 * Also convert literal \n sequences to actual newlines for proper Markdown rendering.
 */
export const stripAnchorTagsAndNormalizeNewlines = (
  content: string
): string => {
  if (!content) return content;
  return content
    .replace(/<a\b[^>]*>.*?<\/a>/gi, "")
    .replace(/<\/?(ul|ol)\b[^>]*>/gi, "")
    .replace(/<li\b[^>]*>/gi, "* ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/?(b|strong|i|em)\b[^>]*>/gi, "")
    .replace(/\\n/g, "\n");
};
